import { BASEURL, extendedFetch } from './network';
import { authFetch } from './authFetch';
import { INTERNAL_STORAGE_KEYS, InternalStorage } from '@utils/storage';
import { TrackingEvent } from '@classes/TrackingEvent';
import { clearSentryUserContext } from '@utils/sentry';
import { AuthTokenManager } from '@classes/AuthTokenManager';

const SIGN_OUT_TIMEOUT_MS = 5000;

async function fetchCSRFTokenForUnauthedRequest() {
  try {
    const response = await extendedFetch(`${BASEURL}/api/v1/util/csrf`, 'GET');
    const data = await response.json();
    return data?.csrf_token ?? null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function signIn({ email, password, rememberMe }) {
  try {
    const response = await authFetch('/api/v1/auth/sign_in', 'POST', {
      auth: {
        email,
        password,
        remember_me: rememberMe,
        is_digital_analyst: true,
      },
    });
    const responseData = await response.json();

    if (response.status !== 200) {
      const errorMessage =
        responseData?.error ||
        responseData?.errors?.[0] ||
        'An error occurred, please refresh and try to Sign In again.';
      return { errorMessage };
    }

    const token = responseData?.access_token;
    if (!token) {
      return {
        errorMessage: 'An error occurred, please refresh and try to Sign In again.',
      };
    }

    AuthTokenManager.setAccessToken(token);
    if (rememberMe === '1') {
      InternalStorage.setItemInStorage(INTERNAL_STORAGE_KEYS.EMAIL, email);
    }

    return { isAuthed: true };
  } catch (e) {
    console.error(e);
    return {
      errorMessage: 'An error occurred, please refresh and try to Sign In again.',
    };
  }
}

export async function signInPasswordless({ email }) {
  try {
    const response = await authFetch('/api/v1/authz/sign_in_passwordless', 'POST', { email });
    const data = await response.json();
    if (response.status !== 200) {
      return { errorMessage: data?.error || 'An error occurred. Please try again.' };
    }
    const token = data?.access_token;
    if (token) {
      AuthTokenManager.setAccessToken(token);
      return { isAuthed: true };
    }
    return { ok: true };
  } catch (e) {
    console.error(e);
    return { errorMessage: 'An error occurred. Please try again.' };
  }
}

export async function refresh() {
  return AuthTokenManager.refresh(AuthTokenManager.getAccessToken());
}

export async function exchange(authCode) {
  if (!authCode) return { errorMessage: 'Missing auth_code' };
  try {
    const response = await authFetch('/api/v1/auth/exchange', 'POST', { auth_code: authCode });
    const data = await response.json();
    if (response.status !== 200) {
      return { errorMessage: data?.error || 'SSO sign-in failed.' };
    }
    const token = data?.access_token;
    if (!token) {
      return { errorMessage: 'SSO sign-in failed.' };
    }
    AuthTokenManager.setAccessToken(token);
    return { isAuthed: true };
  } catch (e) {
    console.error(e);
    return { errorMessage: 'SSO sign-in failed.' };
  }
}

export function isSignedIn() {
  return !!AuthTokenManager.getAccessToken();
}

export async function signOut(pageReload) {
  const token = AuthTokenManager.getAccessToken();

  if (token) {
    try {
      const timeoutController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = timeoutController ? setTimeout(() => timeoutController.abort(), SIGN_OUT_TIMEOUT_MS) : null;
      const headers = new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      });
      await fetch(`${BASEURL}/api/v1/auth/sign_out`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
        signal: timeoutController?.signal,
      });
      if (timeoutId) clearTimeout(timeoutId);
    } catch (e) {
      console.error('sign_out network error', e);
    }
  }

  AuthTokenManager.clear();
  AuthTokenManager.markSignedOut();
  clearSentryUserContext();

  if (pageReload) {
    window.location.assign('/');
  }
  return true;
}

export async function signUp({
  firstName,
  lastName,
  email,
  password,
  rememberMe,
  userSignUpComplete,
  userSignUpError,
  recaptchaValue,
}) {
  try {
    const csrf_token = await fetchCSRFTokenForUnauthedRequest();
    const extraProperties = { is_digital_analyst_signup: 1 };

    const response = await fetch(`${BASEURL}/users`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf_token,
      },
      body: JSON.stringify({
        user: {
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          terms_of_service: '1',
          'g-recaptcha-response': recaptchaValue,
          ...extraProperties,
        },
      }),
    });

    const signUpData = await response.json();
    if (signUpData.error) {
      userSignUpError(signUpData.error);
    } else {
      userSignUpComplete();
    }
  } catch (e) {
    console.error(e);
  }
}

export async function forgotPasswordSubmit({ email, recaptchaValue, userPasswordComplete, userPasswordError }) {
  try {
    const csrf_token = await fetchCSRFTokenForUnauthedRequest();

    const response = await fetch(`${BASEURL}/users/password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrf_token,
      },
      body: JSON.stringify({
        user: {
          email,
          'g-recaptcha-response': recaptchaValue,
        },
      }),
    });

    const data = await response.json();

    if (data.error) {
      userPasswordError(data.error);
    } else {
      userPasswordComplete();
    }
  } catch (e) {
    userPasswordError('An Error occured please. Please try again.');
    console.error(e);
  }
}

export async function resetPasswordSubmit({
  password1,
  password2,
  resetPasswordToken,
  userPasswordComplete,
  userPasswordError,
}) {
  try {
    const csrf_token = await fetchCSRFTokenForUnauthedRequest();

    const response = await fetch(`${BASEURL}/users/password`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrf_token,
      },
      body: JSON.stringify({
        user: {
          reset_password_token: resetPasswordToken,
          password: password1,
          password_confirmation: password2,
        },
      }),
    });

    const data = await response.json();

    if (data.error) {
      TrackingEvent.create('Reset Password Error', { 'Error Message': data.error });
      userPasswordError('An error occurred, please try again. If problem persists, please contact support.');
    } else {
      userPasswordComplete();
    }
  } catch (e) {
    userPasswordError('An error occurred, please try again.');
    console.error(e);
  }
}

export async function signUpEmail({ email, userSignUpComplete, userSignUpError }) {
  try {
    const csrf_token = await fetchCSRFTokenForUnauthedRequest();
    const extraProperties = { is_digital_analyst_signup: 1 };

    const response = await fetch(`${BASEURL}/api/v1/lead`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf_token,
      },
      body: JSON.stringify({
        email,
        ...extraProperties,
      }),
    });

    const signUpData = await response.json();

    if (signUpData.error) {
      userSignUpError(signUpData.error);
    } else {
      userSignUpComplete();
    }
  } catch (e) {
    console.error(e);
  }
}
